import { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useQuoteSearchForCustomer } from '@services/quotes/useQuoteSearch';
import { usePolicySearchForCustomer } from '@services/policy/usePolicySearchForCustomer';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { QuoteCard } from '../components/QuoteCard';
import { PolicySection } from '../components/PolicySection';
import { LoadMoreButton } from '../components/LoadMoreButton';
import { useRelatedTransactionsData } from '../hooks/useRelatedTransactionsData';
import { usePaginatedItems } from '../hooks/usePaginatedList';
import { PAGE_SIZE, NON_BOUND_QUOTE_STATUSES } from '../utils/constants';

export const CustomerTab = () => {
  const { quoteId, customerId, hasPolicyTransaction, currentTransactionId, policyId } = useRelatedTransactionsData();

  // Section expand/collapse state
  const [quotesExpanded, setQuotesExpanded] = useState(!hasPolicyTransaction);
  const [policiesExpanded, setPoliciesExpanded] = useState(hasPolicyTransaction);
  const [expandedPolicyId, setExpandedPolicyId] = useState<string | null>(policyId || null);

  // Pagination state
  const [quotesOffset, setQuotesOffset] = useState(0);
  const [policiesOffset, setPoliciesOffset] = useState(0);

  // Fetch quotes for customer (excluding Bound status)
  const {
    data: quotesData,
    isLoading: isLoadingQuotes,
    isFetching: isFetchingQuotes,
  } = useQuoteSearchForCustomer(
    {
      customerId,
      statuses: NON_BOUND_QUOTE_STATUSES,
      sort: '-createdAt',
      pageLimit: PAGE_SIZE,
      pageOffset: quotesOffset,
    },
    !!customerId,
  );

  // Fetch policies for customer
  const {
    data: policiesData,
    isLoading: isLoadingPolicies,
    isFetching: isFetchingPolicies,
  } = usePolicySearchForCustomer(
    {
      customerId,
      sort: '-effectiveOn',
      pageLimit: PAGE_SIZE,
      pageOffset: policiesOffset,
    } as Parameters<typeof usePolicySearchForCustomer>[0],
    !!customerId,
  );

  // Accumulate paginated items with de-duplication
  const allQuotes = usePaginatedItems(quotesData, quotesOffset);
  const allPolicies = usePaginatedItems(policiesData, policiesOffset);

  // Reset pagination and expanded states when customerId changes
  useEffect(() => {
    setQuotesOffset(0);
    setPoliciesOffset(0);
    setExpandedPolicyId(policyId || null);
    setQuotesExpanded(!hasPolicyTransaction);
    setPoliciesExpanded(hasPolicyTransaction);
  }, [customerId, hasPolicyTransaction, policyId]);

  // Update expanded states when hasPolicyTransaction changes (initial load)
  useEffect(() => {
    setQuotesExpanded(!hasPolicyTransaction);
    setPoliciesExpanded(hasPolicyTransaction);
    if (policyId) {
      setExpandedPolicyId(policyId);
    }
  }, [hasPolicyTransaction, policyId]);

  const quotesTotal = quotesData?.pagination?.total || 0;
  const policiesTotal = policiesData?.pagination?.total || 0;

  const hasMoreQuotes = allQuotes.length < quotesTotal;
  const hasMorePolicies = allPolicies.length < policiesTotal;

  const handleLoadMoreQuotes = () => {
    setQuotesOffset((prev) => prev + PAGE_SIZE);
  };

  const handleLoadMorePolicies = () => {
    setPoliciesOffset((prev) => prev + PAGE_SIZE);
  };

  const handleTogglePolicy = (toggledPolicyId: string) => {
    setExpandedPolicyId((prev) => (prev === toggledPolicyId ? null : toggledPolicyId));
  };

  if (!customerId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {/* Quotes Section */}
      <CollapsibleSection
        title='Quotes'
        isExpanded={quotesExpanded}
        onToggle={() => setQuotesExpanded((prev) => !prev)}
        count={quotesTotal}
      >
        {isLoadingQuotes && allQuotes.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : allQuotes.length === 0 ? (
          <Box sx={{ py: 1, pl: 2, color: 'text.secondary', fontSize: '0.875rem', fontWeight: 'normal' }}>
            No unbound quotes found
          </Box>
        ) : (
          <>
            {allQuotes.map((quote) => (
              <QuoteCard key={quote.id} quote={quote} isCurrent={quote.id === quoteId} />
            ))}
            <LoadMoreButton onClick={handleLoadMoreQuotes} isLoading={isFetchingQuotes} hasMore={hasMoreQuotes} />
          </>
        )}
      </CollapsibleSection>

      {/* Policies Section */}
      <CollapsibleSection
        title='Policies'
        isExpanded={policiesExpanded}
        onToggle={() => setPoliciesExpanded((prev) => !prev)}
        count={policiesTotal}
      >
        {isLoadingPolicies && allPolicies.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : allPolicies.length === 0 ? (
          <Box sx={{ py: 1, pl: 2, color: 'text.secondary', fontSize: '0.875rem', fontWeight: 'normal' }}>
            No policies found
          </Box>
        ) : (
          <>
            {allPolicies.map((policy) => (
              <PolicySection
                key={policy.id}
                policy={policy}
                isExpanded={expandedPolicyId === policy.id}
                onToggle={() => handleTogglePolicy(policy.id)}
                currentTransactionId={currentTransactionId}
                currentPolicyId={policyId}
              />
            ))}
            <LoadMoreButton onClick={handleLoadMorePolicies} isLoading={isFetchingPolicies} hasMore={hasMorePolicies} />
          </>
        )}
      </CollapsibleSection>
    </Box>
  );
};
